import {
  Textarea as HeroTextarea,
  TextAreaProps as HeroTextAreaProps,
} from "@heroui/react";
import clsx from "clsx";
import { CounterRow, useHandleInputValueChange } from "ds/utils/input";
import { getTextareaClassNames } from "ds/utils/styles";

type Color = Extract<HeroTextAreaProps["color"], "default">;
type LabelPlacement = Extract<HeroTextAreaProps["labelPlacement"], "outside">;
type BaseClassNames = NonNullable<HeroTextAreaProps["classNames"]>;
type Variant = "bordered" | "borderless";

export type TextAreaProps = Omit<
  HeroTextAreaProps,
  "color" | "labelPlacement" | "variant"
> & {
  color?: Color;
  labelPlacement?: LabelPlacement;
  variant?: Variant;
  ref?: React.Ref<HTMLTextAreaElement>;
};

function mergeClassNames(
  ...classGroups: (Partial<BaseClassNames> | undefined)[]
): BaseClassNames {
  const allKeys = new Set(
    classGroups.flatMap((group) => (group ? Object.keys(group) : [])),
  );
  const merged: Partial<BaseClassNames> = {};

  allKeys.forEach((key) => {
    const classValues = classGroups.map(
      (group) => group?.[key as keyof BaseClassNames],
    );
    (merged[key as keyof BaseClassNames] as string) = clsx(classValues);
  });

  return merged as BaseClassNames;
}

export function Textarea({ ref, ...props }: TextAreaProps) {
  const {
    classNames: userClassNames,
    size,
    variant = "bordered",
    ...rest
  } = props;

  const { handleValueChange, valueLength } = useHandleInputValueChange({
    defaultValue: props.defaultValue,
    maxLength: props.maxLength,
    onValueChange: props.onValueChange,
    value: props.value,
  });

  const defaultClassNames = getTextareaClassNames(size);
  const variantClassNames: Partial<BaseClassNames> =
    variant === "borderless"
      ? {
          input: "bg-transparent p-0",
          inputWrapper: "border-0! bg-transparent ring-0! focus:ring-0!",
        }
      : {};

  const mergedClassNames = mergeClassNames(
    defaultClassNames,
    variantClassNames,
    userClassNames,
  );

  return (
    <HeroTextarea
      ref={ref}
      {...rest}
      classNames={mergedClassNames}
      data-testid={rest.name}
      description={
        <CounterRow
          left={props.description}
          maxLength={props.maxLength}
          value={props.value}
          valueLength={valueLength}
        />
      }
      errorMessage={
        typeof props.errorMessage === "string" ? (
          <CounterRow
            left={props.errorMessage}
            maxLength={props.maxLength}
            value={props.value}
            valueLength={valueLength}
          />
        ) : (
          props.errorMessage
        )
      }
      labelPlacement="outside"
      variant="bordered"
      onValueChange={handleValueChange}
    />
  );
}

Textarea.displayName = "RecareUI.Textarea";
